<template>
  <div class="pro-camera-inventory-table">
    <div class="level mb-3">
      <div class="level-left">
        <button-group />
      </div>
      <div class="level-right">
        <button class="button is-primary" @click="openAddProCameraInventoryModal">
          <span class="icon"><i class="fas fa-plus"></i></span>
          <span>Add Inventory</span>
        </button>
      </div>
    </div>

    <div class="field">
      <div class="select">
        <select :value="filters.status" aria-label="Filter by status" @change="filterByStatus">
          <option value="">All statuses</option>
          <option v-for="status in statuses" :key="status.value" :value="status.value">
            {{ status.label }}
          </option>
        </select>
      </div>
    </div>

    <div class="table-container">
      <table class="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            <th v-for="column in columns" :key="column.label" :aria-sort="ariaSort(column)">
              <button v-if="column.sort" type="button" class="sort-button" @click="sortBy(column.sort)">
                {{ column.label }}
                <span class="icon is-small" aria-hidden="true"><i :class="sortIcon(column)"></i></span>
              </button>
              <template v-else>{{ column.label }}</template>
            </th>
          </tr>
        </thead>

        <transition name="fade" mode="out-in">
          <tbody v-if="isLoadingSync" key="loading" class="skeleton-tbody">
            <tr v-for="n in skeletonRows" :key="`skeleton-${n}`" class="skeleton-row">
              <td v-for="column in columnCount" :key="column"><div class="skeleton skeleton-text"></div></td>
            </tr>
          </tbody>

          <tbody v-else-if="pro_camera_inventories.length > 0" key="data" class="data-tbody">
            <tr v-for="item in pro_camera_inventories" :key="item.id">
              <td>{{ item.id }}</td>
              <td>{{ item.serial_number || "—" }}</td>
              <td class="device-id">{{ item.device_id || "—" }}</td>
              <td>
                <span :class="['tag', statusClass(item.camera_status)]">
                  {{ item.camera_status }}
                </span>
                <span v-if="item.revoked_at" class="tag is-danger ml-1">revoked</span>
              </td>
              <td>
                <span v-if="item.key_fingerprint" :class="['tag', devicePower(item).className]">
                  {{ devicePower(item).label }}
                </span>
                <span v-else>—</span>
              </td>
              <td>
                <template v-if="item.camera_id">
                  {{ item.camera_name || `#${item.camera_id}` }}
                  <p v-if="item.company_name" class="is-size-7 has-text-grey">{{ item.company_name }}</p>
                </template>
                <span v-else>—</span>
              </td>
              <td>{{ item.owner_company_id ? item.owner_company_name || `#${item.owner_company_id}` : "Natilon" }}</td>
              <td>
                {{ item.software_version || "—" }}
                <p v-if="item.hardware_type" class="is-size-7 has-text-grey">{{ item.hardware_type }}</p>
              </td>
              <td>{{ formatDate(item.last_seen_at) || "—" }}</td>
              <td>
                <div class="buttons are-small">
                  <button
                    class="button is-warning is-light"
                    aria-label="Open device"
                    @click="openProCameraInventoryModal(item)"
                  >
                    <span class="icon"><i class="fas fa-edit"></i></span>
                  </button>
                  <!-- The backend refuses to delete an installed device. -->
                  <template v-if="!item.camera_id">
                  <button
                    v-if="confirmDeleteId !== item.id"
                    class="button is-danger is-light"
                    aria-label="Delete device"
                    @click="confirmDeleteId = item.id"
                  >
                    <span class="icon"><i class="fas fa-trash"></i></span>
                  </button>
                  <template v-else>
                    <button class="button is-danger" @click="confirmDelete(item)">
                      Confirm
                    </button>
                    <button class="button" @click="confirmDeleteId = null">
                      Cancel
                    </button>
                  </template>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else key="empty">
            <tr>
              <td :colspan="columnCount" class="has-text-centered empty-state">
                <div class="empty-state-content">
                  <p class="has-text-grey">
                    <span class="icon is-large"><i class="fas fa-camera-retro fa-2x"></i></span>
                  </p>
                  <p class="has-text-grey">No pro camera inventories found</p>
                </div>
              </td>
            </tr>
          </tbody>
        </transition>
      </table>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from "vuex";
import ButtonGroup from "@/components/ButtonGroup.vue";
import { formatDate } from "@/utils/date_converter";
import { DEVICE_STATUSES, devicePower, statusClass } from "@/utils/pro_camera";

const POLL_INTERVAL_MS = 30000;

export default {
  name: "proCameraInventoryList",
  components: { "button-group": ButtonGroup },
  data() {
    return {
      skeletonRows: 10,
      columnCount: 10,
      columns: [
        { label: "ID", sort: "id" },
        { label: "Serial Number", sort: "serial_number" },
        { label: "Device ID" },
        { label: "Status", sort: "status" },
        { label: "Power" },
        { label: "Site" },
        { label: "Owner" },
        { label: "Software" },
        { label: "Last Seen", sort: "last_seen_at" },
        { label: "Actions" },
      ],
      confirmDeleteId: null,
      statuses: DEVICE_STATUSES,
      pollTimer: null,
    };
  },
  computed: {
    ...mapState(["pro_camera_inventories", "isLoadingSync", "filters", "proCameraSort"]),
    ...mapGetters(["activeTabIndex"]),
  },
  methods: {
    ...mapActions([
      "fetchList",
      "openProCameraInventoryModal",
      "openAddProCameraInventoryModal",
      "deleteProCameraInventory",
    ]),
    ...mapMutations(["SET_FILTERS_STATUS", "SET_CURRENT_PAGE", "SET_PRO_CAMERA_SORT"]),
    // A new column starts newest/largest first for IDs and dates, A-Z for text.
    sortBy(by) {
      const { by: current, order } = this.proCameraSort;
      const next = current === by ? (order === "desc" ? "asc" : "desc") : ["id", "last_seen_at"].includes(by) ? "desc" : "asc";
      this.SET_PRO_CAMERA_SORT({ by, order: next });
      this.SET_CURRENT_PAGE({ tabIndex: this.activeTabIndex, page: 1 });
      this.fetchList();
    },
    sortIcon(column) {
      if (this.proCameraSort.by !== column.sort) return "fas fa-sort has-text-grey-lighter";
      return this.proCameraSort.order === "asc" ? "fas fa-sort-up" : "fas fa-sort-down";
    },
    ariaSort(column) {
      if (!column.sort || this.proCameraSort.by !== column.sort) return null;
      return this.proCameraSort.order === "asc" ? "ascending" : "descending";
    },
    devicePower,
    formatDate,
    statusClass,
    filterByStatus(event) {
      this.SET_FILTERS_STATUS(event.target.value);
      this.SET_CURRENT_PAGE({ tabIndex: this.activeTabIndex, page: 1 });
      this.fetchList();
    },
    confirmDelete(item) {
      this.confirmDeleteId = null;
      this.deleteProCameraInventory(item);
    },
  },
  mounted() {
    this.fetchList();
    this.pollTimer = setInterval(() => {
      if (!document.hidden && !this.isLoadingSync) this.fetchList({ silent: true });
    }, POLL_INTERVAL_MS);
  },
  beforeDestroy() {
    clearInterval(this.pollTimer);
  },
};
</script>

<style scoped>
.table-container {
  position: relative;
  min-height: 400px;
}

.device-id { font-family: monospace; }
.sort-button {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  font-weight: inherit;
  cursor: pointer;
}

.skeleton-row { pointer-events: none; }

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 0%, #f8f8f8 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  display: inline-block;
}

.skeleton-text { height: 20px; width: 80%; max-width: 120px; }

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.fade-enter-to, .fade-leave-from { opacity: 1; }

.empty-state { padding: 3rem !important; }
.empty-state-content { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
.empty-state-content .icon { color: #dbdbdb; }

.entity-link { display: inline-flex; align-items: center; gap: 0.25rem; color: inherit; }
.edit-icon { opacity: 0; transition: opacity 0.15s; }
.entity-link:hover .edit-icon { opacity: 1; }
</style>
